// src/lib/facture-statut.js — « Envoyé » ne se dit que si un courriel est parti.
//
// ⚠️ CHEMIN COMPTABLE (CGI 289). Module PUR.
//
// ── Le défaut (2026-09-30) ───────────────────────────────────────────────
// L'écran disait « ✓ Envoyé » dès que `invoices.delivered_at` était posé. Or
// cette colonne est posée par TROIS gestes : l'envoi réel par courriel, la
// petite pastille « Non envoyé » cliquée à la main, et — jusqu'à ce jour —
// l'attribution du numéro d'un brouillon. Les 29 pièces en base l'ont toutes.
// Elle ne prouve donc pas qu'un propriétaire a reçu quoi que ce soit.
//
// ── Ce qui fait preuve ───────────────────────────────────────────────────
//   1. `envoiCourriel` : la DERNIÈRE note « envoye » (hors essai) du JOURNAL
//      DES ENVOIS (table audit_log, écrite par lib/facture-envoi-proprio.js
//      juste après un courriel accepté — décision Dimitri 2026-10-03 : la
//      facture émise n'est jamais écrite). Lue par
//      src/lib/journal-envois-facture.js.
//   2. Pour l'HISTORIQUE (avant ce jour), une seule trace fiable : l'entrée
//      du journal navigateur `fbz8-factlog` écrite par le bouton « Envoyer par
//      Gmail » APRÈS l'envoi, reconnaissable à `emailEnvoye === true` ET un
//      destinataire `emailDest`. L'ancienne bascule manuelle du journal
//      (avant #408) ne touchait que `emailEnvoye`, jamais `emailDest` : une
//      entrée cochée à la main n'a pas de destinataire et ne compte pas.
//      Rapprochement par `invoiceId`, jamais par nom ni par numéro.
//
//   3. `meta.email_envoye === true` : l'ancien envoi (séries 2026-19 à 25).
//
// Sans preuve mais avec delivered_at → « Émise — envoi non suivi » (anciennes
// factures, pastille manuelle, renvoi par l'opérateur dont la trace vit dans
// audit_log) : on ne sait pas si un courriel est vraiment parti.
// Sans rien → « Émise, non envoyée ».

/**
 * Un courriel est-il réellement parti pour cette pièce ?
 * @param {object} f - ligne mappée (mapInvoiceRow) : { invoiceId, envoiCourriel, emailEnvoyeMeta }
 * @param {Array}  [factLogKv] - entrées du journal navigateur fbz8-factlog
 */
export function courrielReellementParti(f, factLogKv = []) {
  if (!f) return false;
  const preuve = f.envoiCourriel;
  if (preuve && typeof preuve === "object" && preuve.at) return true;
  // Factures 2026-19 à 25 : l'ancien envoi écrivait meta.email_envoye=true.
  if (f.emailEnvoyeMeta === true) return true;
  if (!f.invoiceId || !Array.isArray(factLogKv)) return false;
  return factLogKv.some(
    (e) =>
      e &&
      e.invoiceId === f.invoiceId &&
      e.emailEnvoye === true &&
      typeof e.emailDest === "string" &&
      e.emailDest.trim() !== "",
  );
}

/**
 * Le statut à afficher.
 *   brouillon : pas de numéro — ni émis ni envoyé ;
 *   envoyee   : un courriel est réellement parti (preuve) ;
 *   transmise : delivered_at posé SANS preuve de courriel — les factures
 *               anciennes et celles cochées à la main. Le propriétaire l'a
 *               peut-être reçue : on ne dit ni « envoyé » (non prouvé) ni
 *               « non envoyée » (faux pour la plupart) ;
 *   emise     : numérotée, ni envoyée ni transmise.
 * Libellé « Émise — envoi non suivi » : décision Dimitri 2026-10-01 (il
 * remplace « Transmise » : on ne sait pas si ces courriels sont partis). Le
 * code interne `transmise` ne change pas.
 *   incertain : une tentative d'envoi au résultat inconnu (journal ou session) ;
 *   inconnu   : journal des envois illisible, aucune autre preuve.
 * @returns {{ code: 'brouillon'|'envoyee'|'incertain'|'inconnu'|'transmise'|'emise', libelle: string }}
 */
export const LIBELLE_ENVOI_NON_SUIVI = "Émise — envoi non suivi";
export const LIBELLE_ENVOI_INCONNU = "Envoi : —";
export const LIBELLE_ENVOI_INCERTAIN = "Envoi incertain — vérifiez avant de renvoyer";
export function statutFacture(f, factLogKv = []) {
  if (!f || !f.invoice_number) return { code: "brouillon", libelle: "Brouillon" };
  if (courrielReellementParti(f, factLogKv))
    return { code: "envoyee", libelle: "Envoyé" };
  // Envoi incertain (revue argent 2026-10-03) : une tentative est peut-être
  // arrivée chez le propriétaire — ni « Envoyé » ni « non envoyée », et pas
  // de bouton « Envoyer » (HistoriquePanel).
  if (f.envoiIncertain === true) return { code: "incertain", libelle: LIBELLE_ENVOI_INCERTAIN };
  // Journal des envois illisible (2026-10-03) : on ne sait pas — « — »,
  // jamais « non envoyée » (src/lib/journal-envois-facture.js).
  if (f.envoiInconnu === true) return { code: "inconnu", libelle: LIBELLE_ENVOI_INCONNU };
  if (f.emailEnvoye) return { code: "transmise", libelle: LIBELLE_ENVOI_NON_SUIVI };
  return { code: "emise", libelle: "Émise, non envoyée" };
}
